// videoPrepareErrors.ts — the failure vocabulary of videoPrepare, kept OUT of that module so
// the composer can explain a failure without statically importing the encoder library
// (videoPrepare is loaded with a dynamic import, on first use only).

import type { VideoLimits } from '@/core/services/videoUploadService';

export type PrepareFailure =
  | 'unsupported_browser'
  | 'unreadable'
  | 'no_video'
  | 'too_long'
  | 'too_large'
  | 'cancelled';

export class VideoPrepareError extends Error {
  readonly reason: PrepareFailure;
  constructor(reason: PrepareFailure) {
    super(reason);
    this.reason = reason;
    this.name = 'VideoPrepareError';
  }
}

export function isVideoPrepareError(e: unknown): e is VideoPrepareError {
  return e instanceof VideoPrepareError;
}

export function prepareErrorMessage(reason: PrepareFailure, limits?: VideoLimits | null): string {
  switch (reason) {
    case 'unsupported_browser':
      return "This browser can't prepare videos. Try the latest Chrome, Edge or Safari.";
    case 'unreadable':
      return "That file couldn't be read as a video. Try an MP4 or MOV.";
    case 'no_video':
      return "That file doesn't contain a video.";
    case 'too_long':
      return limits
        ? `Videos can be up to ${Math.round(limits.maxSeconds / 60)} minutes on your plan.`
        : 'That video is too long for your plan.';
    case 'too_large':
      return 'That video is too large to post, even after compressing it. Try a shorter clip.';
    case 'cancelled':
      return 'Video removed.';
  }
}
